import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { UiKitModule } from '@serviceplan/ui-kit';
import { DownloadUploadService, setFormatName } from '@thehubz/client/asset-services';
import {
  AssetUIModel,
  ClientCoreModule,
  ClientNotificationHandlerService,
  StorageUtilityLib,
  TableColumn,
  TableColumnType,
  accessAsset,
} from '@thehubz/client/core';
import { Asset, AssetAspectRatio, AssetFileFormat, CreateAssetHistoryDto } from '@thehubz/client/data-access-api';
import { FileUploadService } from '@thehubz/client/file-upload';
import { AnyType } from '@thehubz/shared/model';
import { createDownloadFilename } from '@thehubz/shared/utils';
import { BehaviorSubject, ReplaySubject, Subscription, tap } from 'rxjs';
import { AssetCommonService } from '../../../services/asset-common.service';
import { UploadVariantsComponent } from '../../upload-variants/upload-variants.component';
import { AspectRatioViewComponent } from './components/aspect-ratio-view/aspect-ratio-view.component';

@Component({
  selector: 'thehubz-asset-variants',
  templateUrl: './asset-variants.component.html',
  styleUrl: './asset-variants.component.scss',
  standalone: true,
  imports: [CommonModule, ClientCoreModule, UiKitModule, AspectRatioViewComponent],
})
export class AssetVariantsComponent implements OnInit, OnDestroy {
  @Input() configObject!: AnyType;

  loadingFlag = false;
  assetInfo!: AssetUIModel;
  AccessAsset = accessAsset;
  selectedAspectRatio?: AssetAspectRatio;
  isExpanded = false;
  btnDisabled = false;
  selectedTab = 0;
  docLoading = false;
  uploadInProgress = false;
  sharedResource: AnyType | undefined;

  private assetDataSubjectSubscription?: Subscription;

  dialogRef!: MatDialogRef<UploadVariantsComponent>;
  aspectRatioData = new ReplaySubject<AssetUIModel>(1);
  changeAspectRatioSelection = new ReplaySubject<string | undefined>(1);

  fileFormatData = new BehaviorSubject<AssetFileFormat[]>([]);
  columns: TableColumn[] = [
    {
      columnDef: 'name',
      header: $localize`File name`,
    },
    {
      columnDef: 'assetMedium',
      header: $localize`:medium of the asset:Medium`,
    },
    {
      columnDef: 'size',
      header: $localize`Size`,
      type: TableColumnType.fileSize,
    },
    {
      columnDef: 'ext',
      header: $localize`:file extension:Format`,
    },
  ];

  private aspectRatio?: string;

  constructor(
    private downloadUploadService: DownloadUploadService,
    private clientNotificationHandlerService: ClientNotificationHandlerService,
    private dialog: MatDialog,
    private router: Router,
    private storageUtility: StorageUtilityLib,
    private assetCommonService: AssetCommonService,
  ) {
    this.loadingFlag = true;
  }

  ngOnInit() {
    this.queryParams();
    this.sharedResource = this.storageUtility.getSessionStorage('sharing');
    this.assetDataSubjectSubscription = this.assetCommonService.assetDataSubject$.subscribe((data) => {
      if (data) {
        this.assetInfo = data;
        if (this.assetInfo?.currentVersion) {
          this.aspectRatioData.next(this.assetInfo);
          if (this.aspectRatio) {
            this.selectedAspectRatio = this.assetInfo.currentVersion?.aspectRatios?.find((ar: AnyType) => ar.aspectRatio === this.aspectRatio);
          } else {
            this.selectedAspectRatio = this.assetInfo.currentVersion?.aspectRatios?.[0];
          }
          this.fileFormatData.next(this.selectedAspectRatio?.formats?.map(setFormatName()) ?? []);
        }
        // add column specific to audio file
        if (this.assetInfo.assetType === Asset.AssetTypeEnum.Video) {
          this.columns.splice(2, 0, {
            columnDef: 'audioFile',
            header: $localize`Audio file`,
          });
          this.columns = [...this.columns];
        }
      }
    });
  }

  queryParams() {
    this.assetCommonService
      .queryParamsAspectRatio()
      .pipe(tap((params) => (this.aspectRatio = params)))
      .subscribe();
  }

  selectAspectRatio(selectedAspectRatio?: AssetAspectRatio) {
    this.selectedAspectRatio = selectedAspectRatio;
    this.fileFormatData.next(selectedAspectRatio?.formats?.map(setFormatName()) || []);
  }

  async downloadFileFormat(event: AnyType) {
    if (event.tableData.name && this.selectedAspectRatio) {
      const source = FileUploadService.getKeyForAspectRatio(
        this.assetInfo,
        this.selectedAspectRatio.aspectRatio,
        event.tableData.fileNameRef + '.' + event.tableData.ext,
      );
      this.clientNotificationHandlerService.successNotification($localize`Please wait. the asset is being downloaded`);
      let assetDownloadHistory: CreateAssetHistoryDto | undefined = undefined;
      if (!this.configObject || this.configObject?.callFrom !== 'shareAssets') {
        assetDownloadHistory = await this.assetCommonService.assetDownloadHistory(
          this.assetInfo,
          'Library',
          this.assetInfo.mainFile.version,
          event.tableData.ext,
          event.tableData.fileNameRef,
        );
      }
      const fileName = createDownloadFilename(this.assetInfo.name, event.tableData.ext, event.tableData.fileNameRef);
      return this.downloadUploadService.downloadFile(source, fileName, assetDownloadHistory);
    }
  }

  deleteFileFormat(event: AnyType) {
    const fileName = event.tableData.fileNameRef ? event.tableData.fileNameRef + '.' + event.tableData.ext : '';
    if (fileName && this.selectedAspectRatio) {
      const key = FileUploadService.getKeyForAspectRatio(this.assetInfo, this.selectedAspectRatio.aspectRatio, fileName);
      this.assetCommonService.deleteFileFormat(this.assetInfo._id, fileName, key).subscribe({
        next: (result) => {
          if (result) {
            this.updateAspectRatio(result);
          }
        },
        error: (err) => this.clientNotificationHandlerService.errorNotification(err),
      });
    }
  }

  deleteAspectRatio(aspect: string) {
    if (this.assetInfo.currentVersion) {
      this.assetCommonService.deleteAspectRatio(this.assetInfo._id, this.assetInfo.currentVersion._id, aspect).subscribe({
        next: (result) => {
          if (result) {
            const changeTo = result.aspectRatios?.[0];
            this.changeAspectRatioSelection.next(changeTo?.aspectRatio);
            this.selectAspectRatio(changeTo);
            this.assetInfo.currentVersion = result;
            this.aspectRatioData.next(this.assetInfo);
          }
        },
        error: (err) => this.clientNotificationHandlerService.errorNotification(err),
      });
    }
  }

  /**
   * This function is to edit the file format metadata
   * @param event - It contain file format data object
   */
  editFileFormat(event: AnyType) {
    const assetPickedProperties = (({ _id: assetId, assetType }) => ({ assetId, assetType }))(this.assetInfo);
    this.dialogRef = this.dialog.open(UploadVariantsComponent, {
      data: {
        ...assetPickedProperties,
        formatMetadata: event,
        aspectRatio: this.selectedAspectRatio,
      },
      minWidth: '80%',
    });
    this.dialogRef.disableClose = true;
    this.dialogRef.afterClosed().subscribe((res: AssetAspectRatio) => {
      if (res) {
        this.clientNotificationHandlerService.successNotification($localize`Updated asset file data`);
        this.updateAspectRatio(res);
      }
    });
  }

  /**
   * This method is used to update the aspect ratio of an asset.
   * @param aspect
   */
  private updateAspectRatio(aspect: AssetAspectRatio) {
    // We first obtain the current version of the asset.
    const assetCurrentVersion = this.assetInfo.currentVersion;
    // If the current version of the asset has aspect ratios defined,
    if (assetCurrentVersion?.aspectRatios) {
      // We find the index of the aspect ratio that matches the provided `aspect` object's aspect ratio.
      const arIdx = assetCurrentVersion.aspectRatios.findIndex((ar: AnyType) => ar.aspectRatio === aspect.aspectRatio);
      // If the aspect ratio is found (i.e., the index is not -1),
      if (arIdx !== -1) {
        // If the `formats` property of the provided `aspect` object is empty or undefined,
        if (!aspect.formats?.length) {
          // We remove the aspect ratio from the array using splice.
          assetCurrentVersion.aspectRatios.splice(arIdx, 1);
          // We set the new aspect ratio selection to the first aspect ratio in the array, if it exists.
          const changeTo = assetCurrentVersion.aspectRatios?.[0];
          // Emit aspectRatio string value to child component
          this.changeAspectRatioSelection.next(changeTo?.aspectRatio);
          // We navigate to the asset aspect ratios detail page with the asset's ID.
          const currentUrl = 'assets/detail/' + this.assetInfo._id + '/asset-aspect-ratios';
          this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
            this.router.navigate([currentUrl]);
          });
        } else {
          // If the `formats` property is not empty, we update the aspect ratio at the found index with the provided `aspect` object.
          assetCurrentVersion.aspectRatios[arIdx] = aspect;
        }
        // We emit the updated asset info through the `aspectRatioData` observable.
        this.aspectRatioData.next(this.assetInfo);
      }
      // We call the `selectAspectRatio` method with the provided `aspect` object.
      this.selectAspectRatio(aspect);
    }
  }

  ngOnDestroy(): void {
    this.assetDataSubjectSubscription?.unsubscribe();
  }
}
